import { IconButton } from '@material-ui/core';
import classNames from 'classnames';
import { FC, MouseEvent } from 'react';

import { ReactComponent as DangerIcon } from '~/ui/assets/images/danger.svg';

import { IResolveCrisisCallPayload } from '~/services/api/crisis/types';
import { IDictionary } from '~/services/api/dictionaries/types';
import { ReactComponent as EditIcon } from '~/ui/assets/images/edit.svg';
import { ReactComponent as ViewIcon } from '~/ui/assets/images/eye-red.svg';
import variables from '~/ui/assets/styles/colors.module.scss';
import Avatar from '~/ui/components/common/Avatar';
import Initials from '~/ui/components/common/Initials';
import { isoToFormat } from '~/utils/date/date';
import extractTimeStringFromDate from '~/utils/date/extractTimeStringFromDate';
import CRISIS_LIST_MAP_TABS from '../CrisisListMap/constants';
import CrisisResolveCheckbox from '../CrisisResolveCheckbox/CrisisResolveCheckbox';

import styles from './CrisisListItemDetails.module.scss';

interface IProps {
  photo: string;
  name: string;
  emrMrn: string;
  location: string;
  timeCall: string;
  file?: IDictionary;
  onClick?: () => void;
  handleViewFile?: () => void;
  handleOpenAddCrisisAddressModal?: () => void;
  withEditAddress?: boolean;
  allocatedTeamMembers?: string[];
  timeAllocated?: string;
  visitStartTime?: string;
  coordinates?: { latitude: number; longitude: number };
  crisisCallId?: number;
  resolveCrisisCall?: (data: IResolveCrisisCallPayload) => Promise<number>;
}

const CrisisListItemDetails: FC<IProps> = ({
  crisisCallId,
  photo,
  name,
  emrMrn,
  location,
  timeCall,
  file,
  allocatedTeamMembers,
  timeAllocated,
  visitStartTime,
  onClick,
  handleOpenAddCrisisAddressModal,
  handleViewFile,
  coordinates,
  withEditAddress = false,
  resolveCrisisCall,
}) => {
  const handleEditLocation = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleOpenAddCrisisAddressModal?.();
  };

  const hasEmptyCoordinates = !coordinates.latitude || !coordinates?.longitude;

  return (
    <div className={styles.section}>
      {hasEmptyCoordinates && (
        <div className={styles.emptyCoordinates}>
          <DangerIcon width={24} height={24} color={variables.colorRed} />

          <span>Location not found. Please check the address</span>
        </div>
      )}
      <div
        className={classNames(styles.container, onClick && styles.clickable)}
        onClick={onClick}
        onKeyDown={e => e.key === 'Enter' && onClick?.()}
        role="button"
        tabIndex={0}
      >
        {photo ? (
          <Avatar url={photo} className={styles.avatar} />
        ) : (
          <Initials name={name} className={styles.avatar} />
        )}
        <div className={styles.info}>
          <p className={styles.name}>{name}</p>
          <span className={styles.grayTitle}>Client EMR MRN: {emrMrn}</span>
          <span className={styles.grayTitle}>
            Location: {location}
            {withEditAddress && (
              <IconButton onClick={handleEditLocation} className={styles.actionButton}>
                <EditIcon color={variables.colorRed} width={24} height={24} />
              </IconButton>
            )}
          </span>
          <div className={styles.timeCall}>
            <span>Time Call Received: </span>
            <b>{timeCall ? extractTimeStringFromDate(new Date(timeCall), true) : ''}</b>
          </div>
          <div className={styles.timeCall}>
            <span>Date Call Received: </span>
            <b>{timeCall ? isoToFormat(timeCall) : ''}</b>
          </div>
          {!!file?.id && handleViewFile && (
            <div className={styles.fileName}>
              <span>{file.name}</span>
              <IconButton onClick={handleViewFile} edge="end">
                <ViewIcon width={16} height={16} />
              </IconButton>
            </div>
          )}
          {allocatedTeamMembers && (
            <div className={styles.timeCall}>
              <span>Allocated Team Members: </span>
              <b>{allocatedTeamMembers.join(', ')}</b>
            </div>
          )}
          {timeAllocated && (
            <div className={styles.timeCall}>
              <span>Time Allocated: </span>
              <b>{extractTimeStringFromDate(new Date(timeAllocated), true)}</b>
            </div>
          )}
          {visitStartTime && (
            <div className={styles.timeCall}>
              <span>Visit Start Time: </span>
              <b>{extractTimeStringFromDate(new Date(visitStartTime), true)}</b>
            </div>
          )}
          {!!resolveCrisisCall && (
            <CrisisResolveCheckbox
              resolveCrisisCall={resolveCrisisCall}
              crisisCallId={crisisCallId}
              tab={CRISIS_LIST_MAP_TABS.CRISIS_QUEUE}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CrisisListItemDetails;
